import { create } from "zustand";

import {
  defaultConversionStrength,
  defaultDeNoise,
  defaultPitchShift,
  defaultRemoveBackgroundVocals,
} from "@/constants/defaultValues";

// `removeInstrumental` and `deReverbDeEcho` hold the user's explicit toggle choice, or `null` when they haven't touched it — in which case consumers fall back to the recommendation. The effective boolean is computed in useAudioQuality; nothing mirrors the recommendation back into the store.
type State = {
  pitch: number | [number, number];
  conversionStrength: number;
  removeInstrumental: boolean | null;
  removeBackgroundVocals: boolean;
  deReverbDeEcho: boolean | null;
  deNoise: boolean;
  setPitch: (pitch: number | [number, number]) => void;
  setConversionStrength: (conversionStrength: number) => void;
  setRemoveInstrumental: (value: boolean | null) => void;
  setRemoveBackgroundVocals: (removeBackgroundVocals: boolean) => void;
  setDeReverbDeEcho: (value: boolean | null) => void;
  setDeNoise: (deNoise: boolean) => void;
  resetInstrumentalAndReverb: () => void;
  reset: (simplyUser: boolean) => void;
};

// Conversion hyper parameters are extracted to a Zustand store because they need to be accessed by the tutorial logic
export const useConversionHyperParameters = create<State>()((set) => ({
  pitch: defaultPitchShift,
  conversionStrength: defaultConversionStrength,
  removeInstrumental: null,
  removeBackgroundVocals: defaultRemoveBackgroundVocals,
  deReverbDeEcho: null,
  deNoise: defaultDeNoise,

  setPitch: (pitch: number | [number, number]) => set({ pitch }),
  setConversionStrength: (conversionStrength: number) =>
    set({ conversionStrength }),
  setRemoveInstrumental: (value: boolean | null) =>
    set({ removeInstrumental: value }),
  setRemoveBackgroundVocals: (removeBackgroundVocals: boolean) =>
    set({ removeBackgroundVocals }),
  setDeReverbDeEcho: (value: boolean | null) => set({ deReverbDeEcho: value }),
  setDeNoise: (deNoise: boolean) => set({ deNoise }),
  resetInstrumentalAndReverb: () =>
    set({ removeInstrumental: null, deReverbDeEcho: null }),

  reset: (simplyUser: boolean) =>
    set({
      // If simply user, enable range pitch shift
      pitch:
        simplyUser ? [defaultPitchShift, defaultPitchShift] : defaultPitchShift,
      conversionStrength: defaultConversionStrength,
      removeInstrumental: null,
      removeBackgroundVocals: defaultRemoveBackgroundVocals,
      deReverbDeEcho: null,
      deNoise: defaultDeNoise,
    }),
}));
